import { SlateStatus } from '@betterpool/api-types/games-service';
import useTranslation from 'next-translate/useTranslation';
import { memo, useCallback, useContext } from 'react';

import repeat from '~/common/utils/repeat';
import PicksheetLink from '~/domains/contest/domains/common/components/PicksheetLink/PicksheetLink';
import { getCanMakePicks } from '~/domains/contest/domains/common/utils/pickControl';
import type { EntryStatus } from '~/domains/contest/domains/services/enums/entry-status.enum';
import type { PlayerPoolMember } from '~/domains/contest/domains/services/interfaces/player-pool-member.interface';
import {
  GroupEntryViewsStoreContext,
  useGroupEntryViewSelector,
} from '~/domains/group/state/groupEntryViews.store';
import { Routes } from '~/domains/common/constants/routes';
import { getCdnUrl } from '~/domains/assets/utils/getCdnUrl';

import EntryRow from '../Entry/EntryRow';
import EntryItem from '../Entry/EntryItem';

type MyEntriesEntry = {
  picks: PlayerPoolMember[];
  id: string;
  status: EntryStatus;
  name?: string;
  isPickController?: boolean;
  isMultiOwner?: boolean;
};

type MyEntriesContentProps = {
  contestId: string;
  entries?: MyEntriesEntry[];
  slateStatus?: SlateStatus;
  leagueAlias: string;
  tiersCount: number;
  slateId: string;
  isEntertainmentOnly?: boolean;
  openEntryNameEditModal: (entryInfo: { id: string; name: string }) => void;
};

type EntryRowContentProps = {
  entry: MyEntriesEntry;
  contestId: string;
  slateStatus?: SlateStatus;
  leagueAlias: string;
  tiersCount: number;
  isEntertainmentOnly?: boolean;
  hasGroupEntryViewsStore: boolean;
  getPicksheetHref: (entryId: string) => string;
  openEntryNameEditModal: (entryInfo: { id: string; name: string }) => void;
};

const EntryRowContent = memo(({
  entry,
  contestId,
  slateStatus,
  leagueAlias,
  tiersCount,
  isEntertainmentOnly,
  hasGroupEntryViewsStore,
  getPicksheetHref,
  openEntryNameEditModal,
}: EntryRowContentProps) => {
  const { t } = useTranslation('contest');
  const view = useGroupEntryViewSelector(entry.id);
  // A viewer needing masking always holds a share, so isMultiOwner gates all masking.
  const isOwner =
    entry.isMultiOwner !== true || (hasGroupEntryViewsStore && view?.viewerRole === 'organizer');
  const canMakePicks = getCanMakePicks(entry);
  const name =
    !hasGroupEntryViewsStore || isOwner
      ? entry.name
      : t('gamecenter.common.entryScheduled.groupEntry');

  const options = {
    ...(canMakePicks && {
      editName: {
        title: t('detail.entries.entry.option.editName'),
        onClick: () => openEntryNameEditModal({ id: entry.id, name: entry.name }),
      },
    }),
    ...(isOwner && {
      withdrawal: {
        title: t('detail.entries.entry.option.withdraw'),
        isHidden: slateStatus !== SlateStatus.SCHEDULED,
      },
    }),
    ...(canMakePicks && {
      edit: {
        title:
          slateStatus === SlateStatus.SCHEDULED
            ? t('detail.entries.entry.option.edit')
            : t('detail.entries.entry.option.view'),
        href: getPicksheetHref(entry.id),
      },
    }),
  };

  return (
    <EntryRow
      name={name}
      entryId={entry.id}
      contestId={contestId}
      isEntertainmentOnly={isEntertainmentOnly}
      options={isOwner || canMakePicks ? options : undefined}
    >
      {entry?.picks &&
        entry?.picks.map((pick) => (
          <PicksheetLink
            href={getPicksheetHref(entry.id)}
            isEnabled={getCanMakePicks(entry)}
            key={pick.id}
            $colorInherit
          >
            <EntryItem
              key={pick.id}
              name={pick.name}
              image={getCdnUrl(`/${leagueAlias}/${pick.playerId}.png`)}
            />
          </PicksheetLink>
        ))}
      {!entry?.picks &&
        repeat(tiersCount, (tierIndex) => (
          <PicksheetLink
            href={getPicksheetHref(entry.id)}
            isEnabled={getCanMakePicks(entry)}
            key={tierIndex}
            $colorInherit
          >
            <EntryItem name={`Tier ${tierIndex + 1}`} />
          </PicksheetLink>
        ))}
    </EntryRow>
  );
});
EntryRowContent.displayName = 'EntryRowContent';

function MyEntriesContent({
  contestId,
  entries = [],
  slateStatus,
  slateId,
  leagueAlias,
  tiersCount,
  isEntertainmentOnly,
  openEntryNameEditModal,
}: MyEntriesContentProps) {
  const groupEntryViewsStore = useContext(GroupEntryViewsStoreContext);
  const getPicksheetHref = useCallback(
    (entryId: string) =>
      Routes.gamecenterPicks(contestId, {
        entryId,
        slateId,
      }),
    [contestId, slateId]
  );

  return (
    <>
      {entries.map((entry) => (
        <EntryRowContent
          key={entry.id}
          entry={entry}
          contestId={contestId}
          slateStatus={slateStatus}
          leagueAlias={leagueAlias}
          tiersCount={tiersCount}
          isEntertainmentOnly={isEntertainmentOnly}
          hasGroupEntryViewsStore={groupEntryViewsStore !== null}
          getPicksheetHref={getPicksheetHref}
          openEntryNameEditModal={openEntryNameEditModal}
        />
      ))}
    </>
  );
}

export default MyEntriesContent;
